// Web Push: честное определение доступности. На iPhone push работает только
// в приложении, установленном на главный экран (iOS 16.4+), не в обычной вкладке.
import { env } from './env';
import { isIos, isStandalone, tenantRegistration } from './pwa';

export type PushState =
  | 'unsupported'
  | 'ios-needs-install'
  | 'not-configured'
  | 'denied'
  | 'available'
  | 'subscribed';

export interface PushKeys {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export async function pushState(slug: string): Promise<PushState> {
  if (!env?.VITE_VAPID_PUBLIC_KEY) return 'not-configured';
  if (isIos() && !isStandalone()) return 'ios-needs-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  const reg = await tenantRegistration(slug);
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'available';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Запрашивает разрешение и подписку. Возвращает ключи для сохранения на сервере. */
export async function subscribePush(slug: string): Promise<PushKeys> {
  if (!env?.VITE_VAPID_PUBLIC_KEY) throw new Error('Уведомления не настроены в этой студии');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Уведомления запрещены в настройках браузера');
  const reg = await tenantRegistration(slug);
  if (!reg) throw new Error('Этот браузер не поддерживает уведомления');
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
    }));
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Браузер вернул неполную подписку');
  return { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth };
}

export async function currentSubscription(slug: string): Promise<PushSubscription | null> {
  const reg = await tenantRegistration(slug);
  return (await reg?.pushManager.getSubscription()) ?? null;
}
